import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import type { WeeklySalesReportResponse, WeeklySalesReportItem } from "@/lib/dashboardApi";

// A programmatically-drawn PDF model of the Weekly Sales Report — it mirrors the on-screen
// layout (brand header, KPI cards, sectioned tables) without rasterizing the DOM. Every element
// is laid out as vector text/shapes, so the output is crisp and selectable.

type RGB = [number, number, number];

const COLORS = {
  text: [17, 24, 39] as RGB,
  muted: [107, 114, 128] as RGB,
  border: [229, 231, 235] as RGB,
  primary: [176, 137, 43] as RGB, // High Class gold
  emerald: [5, 150, 105] as RGB,
  emeraldBg: [209, 250, 229] as RGB,
  blue: [37, 99, 235] as RGB,
  blueBg: [219, 234, 254] as RGB,
  rose: [225, 29, 72] as RGB,
  roseBg: [255, 228, 230] as RGB,
  amber: [217, 119, 6] as RGB,
  amberBg: [254, 243, 199] as RGB,
  staleBg: [254, 226, 226] as RGB,
  zebra: [249, 250, 251] as RGB,
};

const MARGIN = 40;
const STALE_DAYS = 5;

const money = (value?: number | null, currency?: string | null) => {
  const amount = typeof value === "number" ? value : 0;
  const cur = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const asDate = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy");
};

const asDateTime = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy 'at' HH:mm");
};

const proposalCell = (item: WeeklySalesReportItem) => {
  const sub = [item.internal_reference, item.client_name && item.client_name !== "Unknown" ? item.client_name : null]
    .filter(Boolean)
    .join(" · ");
  return sub ? `${item.title || "Untitled"}\n${sub}` : item.title || "Untitled";
};

const followUpCell = (item: WeeklySalesReportItem) => {
  const when = item.last_follow_up_at ? asDateTime(item.last_follow_up_at) : "Never";
  const days = typeof item.days_since_last_follow_up === "number" ? `  (${item.days_since_last_follow_up}d)` : "";
  const msg = item.last_follow_up_message ? `\n“${item.last_follow_up_message}”` : "";
  return `${when}${days}${msg}`;
};

const nextY = (doc: jsPDF, fallback: number) => {
  const last = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable;
  return last ? last.finalY : fallback;
};

export const buildWeeklySalesReportPdf = (report: WeeklySalesReportResponse): jsPDF => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - MARGIN * 2;

  const weekLabel = `${asDate(report.week_start)} – ${asDate(report.week_end)}`;

  // ---- Brand header ----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.text);
  doc.text("Weekly Sales Report", MARGIN, MARGIN + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.muted);
  doc.text(weekLabel, MARGIN, MARGIN + 24);

  doc.setFontSize(9);
  doc.setTextColor(...COLORS.primary);
  doc.text("HIGH CLASS TRAVEL EXPERIENCES", pageW - MARGIN, MARGIN + 6, { align: "right" });

  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, MARGIN + 34, pageW - MARGIN, MARGIN + 34);

  // ---- KPI cards ----
  const grandTotal = (report.new_proposals.total_value || 0) + (report.older_active_proposals.total_value || 0);
  const kpis: { accent: RGB; accentBg: RGB; label: string; value: string; sub: string }[] = [
    {
      accent: COLORS.emerald, accentBg: COLORS.emeraldBg, label: "NEW THIS WEEK",
      value: String(report.new_proposals.count), sub: money(report.new_proposals.total_value, "USD"),
    },
    {
      accent: COLORS.blue, accentBg: COLORS.blueBg, label: "OPEN (OLDER) PIPELINE",
      value: String(report.older_active_proposals.count), sub: money(report.older_active_proposals.total_value, "USD"),
    },
    {
      accent: COLORS.rose, accentBg: COLORS.roseBg, label: "CANCELED THIS WEEK",
      value: String(report.canceled.count), sub: `${report.canceled.internal.count} internal · ${report.canceled.external.count} external`,
    },
    {
      accent: COLORS.primary, accentBg: [245, 236, 214], label: "PIPELINE VALUE",
      value: money(grandTotal, "USD"), sub: "New + open proposals",
    },
  ];

  const gap = 12;
  const cardW = (contentW - gap * 3) / 4;
  const cardH = 58;
  const cardY = MARGIN + 46;
  kpis.forEach((kpi, i) => {
    const x = MARGIN + i * (cardW + gap);
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(x, cardY, cardW, cardH, 5, 5, "S");
    // accent chip
    doc.setFillColor(...kpi.accentBg);
    doc.roundedRect(x + 10, cardY + 12, 10, 10, 2, 2, "F");
    doc.setFillColor(...kpi.accent);
    doc.roundedRect(x + 13, cardY + 15, 4, 4, 1, 1, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...COLORS.muted);
    doc.text(kpi.label, x + 26, cardY + 18);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.text(kpi.value, x + 11, cardY + 38);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.muted);
    doc.text(doc.splitTextToSize(kpi.sub, cardW - 20), x + 11, cardY + 50);
  });

  let cursorY = cardY + cardH + 22;

  const sectionHeader = (title: string, accent: RGB, count: number, total?: number) => {
    if (cursorY > doc.internal.pageSize.getHeight() - 120) {
      doc.addPage();
      cursorY = MARGIN + 10;
    }
    doc.setFillColor(...accent);
    doc.roundedRect(MARGIN, cursorY - 9, 4, 14, 1, 1, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    doc.text(`${title}  (${count})`, MARGIN + 12, cursorY + 2);
    if (typeof total === "number") {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.muted);
      doc.text(`Total: ${money(total, "USD")}`, pageW - MARGIN, cursorY + 2, { align: "right" });
    }
    cursorY += 10;
  };

  const baseTableOptions = {
    theme: "plain" as const,
    headStyles: { fillColor: COLORS.zebra, textColor: COLORS.muted, fontSize: 7, fontStyle: "bold" as const, lineColor: COLORS.border, lineWidth: { bottom: 0.8 } },
    bodyStyles: { fontSize: 8, textColor: COLORS.text, cellPadding: { top: 4, bottom: 4, left: 5, right: 5 }, lineColor: COLORS.border, lineWidth: { bottom: 0.4 } },
    alternateRowStyles: { fillColor: [255, 255, 255] as RGB },
    margin: { left: MARGIN, right: MARGIN },
  };

  const emptyNote = (text: string) => {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(text, MARGIN + 12, cursorY + 14);
    cursorY += 26;
  };

  // ---- New proposals ----
  sectionHeader("New proposals this week", COLORS.emerald, report.new_proposals.count, report.new_proposals.total_value);
  if (report.new_proposals.items.length === 0) {
    emptyNote("No new proposals this week.");
  } else {
    autoTable(doc, {
      ...baseTableOptions,
      startY: cursorY + 4,
      head: [["Date", "Proposal", "Owner", "Value"]],
      body: report.new_proposals.items.map((it) => [asDate(it.created_at), proposalCell(it), it.owner_name || "—", money(it.value, it.currency)]),
      columnStyles: {
        0: { cellWidth: 70 },
        2: { cellWidth: 90 },
        3: { cellWidth: 75, halign: "right", fontStyle: "bold" },
      },
    });
    cursorY = nextY(doc, cursorY) + 18;
  }

  // ---- Follow-up watch ----
  sectionHeader("Follow-up watch (older open proposals)", COLORS.blue, report.older_active_proposals.count, report.older_active_proposals.total_value);
  if (report.older_active_proposals.items.length === 0) {
    emptyNote("No older open proposals.");
  } else {
    autoTable(doc, {
      ...baseTableOptions,
      startY: cursorY + 4,
      head: [["Proposal", "Owner", "Last follow-up", "Value"]],
      body: report.older_active_proposals.items.map((it) => [proposalCell(it), it.owner_name || "—", followUpCell(it), money(it.value, it.currency)]),
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 70 },
        2: { cellWidth: "auto" },
        3: { cellWidth: 70, halign: "right", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        const item = report.older_active_proposals.items[data.row.index];
        const stale = item && typeof item.days_since_last_follow_up === "number" && item.days_since_last_follow_up > STALE_DAYS;
        if (data.section === "body" && stale) {
          data.cell.styles.fillColor = COLORS.staleBg;
          if (data.column.index === 2) data.cell.styles.textColor = COLORS.rose;
        }
      },
    });
    cursorY = nextY(doc, cursorY) + 18;
  }

  // ---- Cancellations ----
  sectionHeader("Cancellations this week", COLORS.rose, report.canceled.count, report.canceled.total_value);
  cursorY += 4;

  const cancellationSub = (label: string, accent: RGB, section: typeof report.canceled.internal) => {
    if (cursorY > doc.internal.pageSize.getHeight() - 90) {
      doc.addPage();
      cursorY = MARGIN + 10;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...accent);
    doc.text(`${label}  —  ${section.count} · ${money(section.total_value, "USD")}`, MARGIN + 12, cursorY + 10);
    cursorY += 14;
    if (section.items.length === 0) {
      emptyNote("None.");
      return;
    }
    autoTable(doc, {
      ...baseTableOptions,
      startY: cursorY + 2,
      head: [["Canceled", "Proposal", "Owner", "Value"]],
      body: section.items.map((it) => [asDate(it.canceled_at), proposalCell(it), it.owner_name || "—", money(it.value, it.currency)]),
      columnStyles: {
        0: { cellWidth: 70 },
        2: { cellWidth: 90 },
        3: { cellWidth: 75, halign: "right", fontStyle: "bold" },
      },
    });
    cursorY = nextY(doc, cursorY) + 14;
  };

  cancellationSub("Internal interference", COLORS.rose, report.canceled.internal);
  cancellationSub("External interference", COLORS.amber, report.canceled.external);

  drawFooters(doc, report);
  return doc;
};

export const downloadWeeklySalesReportPdf = (report: WeeklySalesReportResponse) => {
  const doc = buildWeeklySalesReportPdf(report);
  doc.save(`weekly-sales-report_${report.week_start}.pdf`);
};

const drawFooters = (doc: jsPDF, report: WeeklySalesReportResponse) => {
  const pageW = doc.internal.pageSize.getWidth();
  // ---- Footer on every page ----
  const pageCount = doc.getNumberOfPages();
  const generated = `Generated ${asDate(report.generated_at)}`;
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    const h = doc.internal.pageSize.getHeight();
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, h - 28, pageW - MARGIN, h - 28);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.muted);
    doc.text(`${generated} · High Class Travel Experiences`, MARGIN, h - 16);
    doc.text(`Page ${p} of ${pageCount}`, pageW - MARGIN, h - 16, { align: "right" });
  }
};
